/**
 * Utility functions for MV/AV conversion
 * Handles the case where one of EQ or RAR might be zero
 */

/**
 * Locate a case on negotiationObj.cases by parcel id (matches parcel_id or id, string-normalized).
 * @param {{ cases?: unknown[] } | null | undefined} negotiationObj
 * @param {string|number|null|undefined} parcelId
 * @returns {object|undefined}
 */
export const findNegotiationCaseByParcelId = (negotiationObj, parcelId) => {
  if (parcelId == null || parcelId === '') return undefined;
  const pid = String(parcelId);
  const cases = negotiationObj?.cases;
  if (!Array.isArray(cases)) return undefined;
  return cases.find((x) => String(x?.parcel_id ?? x?.id ?? '') === pid);
};

/**
 * MV/AV conversion factor from the negotiation case for this parcel (EQ/RAR on that case).
 * @param {{ cases?: unknown[] } | null | undefined} negotiationObj
 * @param {string|number|null|undefined} parcelId
 * @returns {number}
 * @throws {Error} if case missing or EQ/RAR invalid per getConversionFactor
 */
export const getConversionFactorForNegotiationParcel = (negotiationObj, parcelId) => {
  const c = findNegotiationCaseByParcelId(negotiationObj, parcelId);
  if (!c) throw new Error('[conversionUtils] Missing negotiation case for parcelId.');
  return getConversionFactor(c.EQ, c.RAR);
};

/**
 * Get the correct conversion factor for MV to AV conversion
 * If both EQ and RAR are available, use the minimum
 * If only one is available (the other is 0), use the available one
 * If both are 0, return 0 (no conversion possible)
 * 
 * @param {number} eq - Equalization rate
 * @param {number} rar - Residential Assessment Ratio
 * @returns {number} The conversion factor to use
 */
export const getConversionFactor = (eq, rar) => {
  if (rar == null) throw new Error('[conversionUtils] Missing RAR.');
  const rarValue = Number(rar);
  const eqValue = eq == null ? rarValue : Number(eq);
  if (!Number.isFinite(rarValue) || !Number.isFinite(eqValue)) {
    throw new Error('[conversionUtils] Invalid EQ/RAR (must be finite numbers).');
  }
  
  // If both are available, use the minimum
  if (eqValue > 0 && rarValue > 0) {
    // TEMPORARILY NOT TAKING MINIMUM - the querys ifmv calc has to be IN LINE
    // return Math.min(eqValue, rarValue);
    return rarValue;
  }
  
  // If only one is available, use that one
  if (eqValue > 0) {
    return eqValue;
  }
  
  if (rarValue > 0) {
    return rarValue;
  }
  
  // If both are 0, no conversion possible (explicitly fail)
  throw new Error('[conversionUtils] Cannot derive conversion factor from EQ/RAR.');
};

/**
 * Convert Market Value to Assessment Value
 * @param {number} marketValue - The market value to convert
 * @param {number} eq - Equalization rate
 * @param {number} rar - Residential Assessment Ratio
 * @returns {number} The assessment value
 */
export const convertMVToAV = (marketValue, eq, rar) => {
  if (!marketValue || marketValue <= 0) return marketValue;
  
  const conversionFactor = getConversionFactor(eq, rar);
  if (conversionFactor <= 0) throw new Error('[conversionUtils] Invalid conversion factor for MV→AV.');
  
  return Math.round(marketValue * conversionFactor);
};

/**
 * Convert Assessment Value to Market Value
 * @param {number} assessmentValue - The assessment value to convert
 * @param {number} eq - Equalization rate
 * @param {number} rar - Residential Assessment Ratio
 * @returns {number} The market value
 */
export const convertAVToMV = (assessmentValue, eq, rar) => {
  if (!assessmentValue) return assessmentValue;
  
  const conversionFactor = getConversionFactor(eq, rar);
  if (conversionFactor <= 0) throw new Error('[conversionUtils] Invalid conversion factor for AV→MV.');
  
  return Math.round(assessmentValue / conversionFactor);
};
